<template>
  <div class="mt-3">
    <div class="iq-card mb-3 mx-3" style="min-width: 300px;">
      <div class="iq-card-header d-flex justify-content-between">
        <div class="iq-header-title py-3">
          <h4 class="card-title">Edit Role</h4>
        </div>
      </div>
      <div class="iq-card-body">
        <div class="row">
          <div role="group" class="form-group col-md-6">
            <label for="roleName" class="d-block form-label text-left">
              Name:
            </label>
            <div class="bv-no-focus-ring">
              <div role="group" class="input-group">
                <input class="form-control" type="text" id="roleName" placeholder="Role name"
                  v-model="data.roleName" :class="errors.roleName ? 'is-invalid' : ''">
                <b-form-invalid-feedback v-if="errors.roleName">
                  {{ Object.values(errors.roleName) }}
                </b-form-invalid-feedback>
              </div>
            </div>
          </div>
          <div role="group" class="form-group col-md-6">
            <label class="d-block form-label text-left">
              Copy Permissions From Role:
            </label>
            <div class="d-flex" style="gap: .5rem;">
              <v-select style="flex: 1 1 auto;" label="roleName" placeholder="Select role"
                v-model="copySourceId" :disabled="isCopying"
                :options="copyRoleOptions" :reduce="option => option.roleId" />
              <b-button size="sm" variant="primary" :disabled="isCopying || !copySourceId"
                @click="onCopyClicked">
                <div class="mx-2 text-nowrap">
                  <i class="fa" :class="isCopying ? 'ri-loader-line' : 'ri-file-copy-line'"></i>
                  Copy
                </div>
              </b-button>
            </div>
          </div>
        </div>
        <div class="form-group text-right">
          <b-button type="submit" @click="onSubmitEditClicked('Updated')" :disabled="isSubmit" variant="primary"
            size="sm" class="ml-1">
            <div class="mx-2">
              <i class="fa" :class="isSubmit ? 'ri-loader-line' : 'ri-save-line'"></i>
              Update Role Data
            </div>
          </b-button>
        </div>
      </div>
    </div>
    <div class="px-3">
      <relation-manager ref="relationManager" :config="permissionRelation" :parent-id="id" />
    </div>
    <b-modal
      size="lg"
      hide-footer
      :title="'Copy Permissions from ' + copySourceName"
      v-model="displayCopyModal">
      <p class="text-left">
        Select the permissions to copy. Permissions this role already has are not listed.
      </p>
      <div class="text-left mb-2">
        <b-button size="sm" variant="outline-primary" :disabled="isCopying"
          @click="copySelectedIds = copyOptions.map((option) => option.value)">
          Select All
        </b-button>
        <b-button size="sm" variant="outline-primary" class="ml-1" :disabled="isCopying"
          @click="copySelectedIds = []">
          Deselect All
        </b-button>
      </div>
      <b-form-checkbox-group v-model="copySelectedIds" :options="copyOptions" stacked
        class="text-left" :disabled="isCopying" />
      <div class="text-right mt-3">
        <b-button variant="light" size="sm" :disabled="isCopying" @click="displayCopyModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" size="sm" class="ml-1"
          :disabled="isCopying || !copySelectedIds.length" @click="onConfirmCopy">
          <i class="fa" :class="isCopying ? 'ri-loader-line' : 'ri-file-copy-line'"></i>
          Copy {{ copySelectedIds.length }} Permission{{ copySelectedIds.length === 1 ? '' : 's' }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import RoleSvc from '@/services/RoleSvc';
import requestBuilder from '@/util/master/request.builder';

import RelationManager from '@/components/shared/RelationManager';
import { permissionRelation } from '@/components/userrole/role/relation-configs';

export default {
  name: 'RoleDetail',
  components: { RelationManager },
  computed: {
    permissionRelation: () => permissionRelation,
    copySourceName() {
      const found = this.copyRoleOptions.find((role) => role.roleId === this.copySourceId);
      return found ? found.roleName : '';
    },
    copyOptions() {
      return this.copyCandidates.map((row) => ({
        value: row.rperPrms.prmsId,
        text: `${row.rperPrms.prmsName} (${JSON.parse(row.rperOperations).join(', ') || 'no operations'})`,
      }));
    },
  },
  data() {
    return {
      data: {},
      errors: {},
      filter: {
        roleId: this.$route.params.id,
      },
      id: this.$route.params.id,
      isSubmit: false,
      isCopying: false,
      copySourceId: null,
      copyRoleOptions: [],
      displayCopyModal: false,
      copyCandidates: [],
      copySelectedIds: [],
      pagination: {
        current: 1,
        pageSize: 1,
        totalSize: 0,
      },
    };
  },
  methods: {
    async getData(notification) {
      try {
        const res = await RoleSvc.getRoles({
          params: { ...requestBuilder.buildFilters(this.pagination, this.filter) },
        });
        this.data = res.data.data?.[0] || {};
        if (notification) this.$toast.success(notification);
      } catch (e) {
        this.$toast.error('Error when fetching role data');
      }
    },
    async getRoleOptions() {
      try {
        // ponytail: role list is bounded (same 500 ceiling as permission fetch in relation-configs)
        const res = await RoleSvc.getRoles({
          params: { ...requestBuilder.buildFilters({ current: 1, pageSize: 500 }, {}) },
        });
        this.copyRoleOptions = res.data.data.filter(
          (role) => String(role.roleId) !== String(this.id),
        );
      } catch (e) {
        this.$toast.error('Error when fetching role list');
      }
    },
    async onCopyClicked() {
      this.isCopying = true;
      try {
        const pagination = { current: 1, pageSize: 500 };
        const [srcRes, ownRes] = await Promise.all([
          RoleSvc.getRolePermissions({
            params: { ...requestBuilder.buildFilters(pagination, { rperRoleId: this.copySourceId }) },
          }),
          RoleSvc.getRolePermissions({
            params: { ...requestBuilder.buildFilters(pagination, { rperRoleId: this.id }) },
          }),
        ]);
        const ownedIds = new Set(ownRes.data.data.map((row) => row.rperPrms.prmsId));
        this.copyCandidates = srcRes.data.data.filter((row) => !ownedIds.has(row.rperPrms.prmsId));
        if (!this.copyCandidates.length) {
          this.$toast.success('Nothing to copy — this role already has all permissions of the source role');
          return;
        }
        this.copySelectedIds = this.copyCandidates.map((row) => row.rperPrms.prmsId);
        this.displayCopyModal = true;
      } catch (e) {
        this.$toast.error('Error when fetching permissions to copy');
      } finally {
        this.isCopying = false;
      }
    },
    async onConfirmCopy() {
      this.isCopying = true;
      try {
        const rows = this.copyCandidates.filter(
          (row) => this.copySelectedIds.includes(row.rperPrms.prmsId),
        );
        // rperOperations dari API masih JSON string, langsung diterusin apa adanya
        await Promise.all(rows.map((row) => RoleSvc.addRolePermission({
          rperRoleId: this.id,
          rperPrmsId: row.rperPrms.prmsId,
          rperOperations: row.rperOperations,
        })));
        this.$toast.success(`${rows.length} permission copied`);
        this.displayCopyModal = false;
        this.copySourceId = null;
        await this.$refs.relationManager.getAssigned();
      } catch (e) {
        this.$toast.error('Error when copying permissions');
      } finally {
        this.isCopying = false;
      }
    },
    async onSubmitEditClicked(notification) {
      this.errors = {};
      this.isSubmit = true;
      try {
        await RoleSvc.update(this.id, { roleName: this.data.roleName });
        await this.getData(notification);
      } catch (e) {
        this.$toast.error('Error when submitting');
      } finally {
        this.isSubmit = false;
      }
    },
  },
  async mounted() {
    await Promise.all([this.getData(), this.getRoleOptions()]);
  },
};
</script>

<style scoped></style>
